/**
 * Editor canvas view: free camera (pan/zoom), block grid, object rendering,
 * selection highlights, placement ghost, selection box. Screen-space is
 * y-down; world-space is y-up (same convention as the game renderer).
 *
 * NOTE: object shapes here are simplified editor representations. Phase 4
 * consolidates game/editor object drawing behind one atlas-based pipeline
 * (tracked in ROADMAP Phase 4).
 */
import type { LevelData, LevelObject } from '../format/types';
import { UNITS_PER_BLOCK as B } from '../engine/physics/constants';

export interface EditorCamera {
  /** World coords of the view center. */
  x: number;
  y: number;
  /** Pixels per world unit. */
  zoom: number;
}

export interface Box {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Diagnostics overlays (computed by the controller from the REAL runtime
 * collision/trigger data, so what you see is what the engine uses). */
export interface OverlayData {
  solids: Box[];
  hazards: Box[];
  zones: Box[];
  flows: Array<{ x0: number; y0: number; x1: number; y1: number; color: string }>;
}

export interface ViewState {
  camera: EditorCamera;
  selection: ReadonlySet<number>;
  /** Placement ghost (build mode). */
  ghost: { obj: LevelObject } | null;
  /** Selection box in world coords, or null. */
  selectionBox: { x0: number; y0: number; x1: number; y1: number } | null;
  showGrid: boolean;
  overlay: OverlayData | null;
  /** World units per beat (BPM guides); null = hidden. */
  bpmSpacing: number | null;
}

const PORTAL_COLORS: Record<string, string> = {
  'gravity:up': '#fde047',
  'gravity:down': '#60a5fa',
  'size:mini': '#f9a8d4',
  'size:normal': '#e2e8f0',
  teleport: '#34d399',
};

function portalColor(kind: string): string {
  if (kind.startsWith('form:')) return '#22d3ee';
  if (kind.startsWith('speed:')) return '#4ade80';
  if (kind.startsWith('mirror:')) return '#38bdf8';
  if (kind.startsWith('dual:')) return '#fb923c';
  return PORTAL_COLORS[kind] ?? '#e2e8f0';
}

export class EditorView {
  private ctx: CanvasRenderingContext2D;

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas2D context unavailable');
    this.ctx = ctx;
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(this.canvas.clientWidth * dpr);
    this.canvas.height = Math.round(this.canvas.clientHeight * dpr);
  }

  /** World → screen (canvas pixels). */
  toScreen(cam: EditorCamera, wx: number, wy: number): { x: number; y: number } {
    return {
      x: this.canvas.width / 2 + (wx - cam.x) * cam.zoom,
      y: this.canvas.height / 2 - (wy - cam.y) * cam.zoom,
    };
  }

  /** Screen (canvas pixels) → world. */
  toWorld(cam: EditorCamera, sx: number, sy: number): { x: number; y: number } {
    return {
      x: cam.x + (sx - this.canvas.width / 2) / cam.zoom,
      y: cam.y - (sy - this.canvas.height / 2) / cam.zoom,
    };
  }

  /** Convert a pointer event's client coords to canvas pixels. */
  clientToCanvas(e: { clientX: number; clientY: number }): { x: number; y: number } {
    const r = this.canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - r.left) / r.width) * this.canvas.width,
      y: ((e.clientY - r.top) / r.height) * this.canvas.height,
    };
  }

  draw(level: LevelData, vs: ViewState): void {
    const { ctx, canvas } = this;
    const cam = vs.camera;

    ctx.fillStyle = level.settings.backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.scale(cam.zoom, -cam.zoom);
    ctx.translate(-cam.x, -cam.y);

    const halfW = canvas.width / 2 / cam.zoom;
    const halfH = canvas.height / 2 / cam.zoom;
    const minX = cam.x - halfW;
    const maxX = cam.x + halfW;
    const minY = cam.y - halfH;
    const maxY = cam.y + halfH;

    // Ground line + grid.
    if (vs.showGrid) this.drawGrid(minX, maxX, minY, maxY, cam.zoom);
    if (vs.bpmSpacing !== null && vs.bpmSpacing > 4) {
      // Beat guides: brighter every 4th line (bar starts).
      ctx.lineWidth = 1 / cam.zoom;
      const first = Math.floor(minX / vs.bpmSpacing);
      const last = Math.ceil(maxX / vs.bpmSpacing);
      for (let i = Math.max(0, first); i <= last; i++) {
        const x = i * vs.bpmSpacing;
        ctx.strokeStyle = i % 4 === 0 ? 'rgba(240, 171, 252, 0.5)' : 'rgba(240, 171, 252, 0.2)';
        ctx.beginPath();
        ctx.moveTo(x, minY);
        ctx.lineTo(x, maxY);
        ctx.stroke();
      }
    }
    ctx.strokeStyle = level.settings.accentColor;
    ctx.lineWidth = 2 / cam.zoom;
    ctx.beginPath();
    ctx.moveTo(minX, 0);
    ctx.lineTo(maxX, 0);
    ctx.stroke();

    // Objects (culled).
    for (const obj of level.objects) {
      if (obj.x < minX - 2 * B || obj.x > maxX + 2 * B) continue;
      if (obj.y < minY - 4 * B || obj.y > maxY + 4 * B) continue;
      this.drawObject(obj, level, false);
      if (vs.selection.has(obj.id)) this.drawSelectionOutline(obj, cam.zoom);
    }

    // Diagnostics overlays: hitboxes + trigger flow.
    if (vs.overlay) this.drawOverlay(vs.overlay, cam.zoom, minX, maxX);

    // Placement ghost.
    if (vs.ghost) {
      ctx.save();
      ctx.globalAlpha = 0.45;
      this.drawObject(vs.ghost.obj, level, true);
      ctx.restore();
    }

    // Selection box.
    if (vs.selectionBox) {
      const b = vs.selectionBox;
      ctx.strokeStyle = '#67e8f9';
      ctx.setLineDash([6 / cam.zoom, 4 / cam.zoom]);
      ctx.lineWidth = 1.5 / cam.zoom;
      ctx.strokeRect(
        Math.min(b.x0, b.x1),
        Math.min(b.y0, b.y1),
        Math.abs(b.x1 - b.x0),
        Math.abs(b.y1 - b.y0),
      );
      ctx.setLineDash([]);
      ctx.save();
      ctx.globalAlpha = 0.1;
      ctx.fillStyle = '#67e8f9';
      ctx.fillRect(
        Math.min(b.x0, b.x1),
        Math.min(b.y0, b.y1),
        Math.abs(b.x1 - b.x0),
        Math.abs(b.y1 - b.y0),
      );
      ctx.restore();
    }

    ctx.restore();
  }

  private drawOverlay(overlay: OverlayData, zoom: number, minX: number, maxX: number): void {
    const { ctx } = this;
    ctx.save();
    ctx.lineWidth = 1.5 / zoom;
    const rects = (boxes: Box[], color: string): void => {
      ctx.strokeStyle = color;
      for (const b of boxes) {
        if (b.maxX < minX || b.minX > maxX) continue;
        ctx.strokeRect(b.minX, b.minY, b.maxX - b.minX, b.maxY - b.minY);
      }
    };
    rects(overlay.solids, 'rgba(96, 165, 250, 0.9)');
    rects(overlay.hazards, 'rgba(248, 113, 113, 0.95)');
    rects(overlay.zones, 'rgba(74, 222, 128, 0.7)');
    for (const f of overlay.flows) {
      if (Math.max(f.x0, f.x1) < minX || Math.min(f.x0, f.x1) > maxX) continue;
      ctx.strokeStyle = f.color;
      ctx.globalAlpha = 0.65;
      ctx.beginPath();
      ctx.moveTo(f.x0, f.y0);
      // Slight arc so overlapping flows stay readable.
      ctx.quadraticCurveTo((f.x0 + f.x1) / 2, Math.max(f.y0, f.y1) + 40, f.x1, f.y1);
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.fillStyle = f.color;
      ctx.beginPath();
      ctx.arc(f.x1, f.y1, 3 / Math.sqrt(zoom), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  private drawGrid(minX: number, maxX: number, minY: number, maxY: number, zoom: number): void {
    const { ctx } = this;
    if (zoom < 0.12) return; // grid unreadable when zoomed far out
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.15)';
    ctx.lineWidth = 1 / zoom;
    const x0 = Math.floor(minX / B) * B;
    const y0 = Math.floor(minY / B) * B;
    ctx.beginPath();
    for (let x = x0; x <= maxX; x += B) {
      ctx.moveTo(x, minY);
      ctx.lineTo(x, maxY);
    }
    for (let y = y0; y <= maxY; y += B) {
      ctx.moveTo(minX, y);
      ctx.lineTo(maxX, y);
    }
    ctx.stroke();
  }

  private drawSelectionOutline(obj: LevelObject, zoom: number): void {
    const { ctx } = this;
    ctx.strokeStyle = '#facc15';
    ctx.lineWidth = 2 / zoom;
    ctx.setLineDash([5 / zoom, 3 / zoom]);
    ctx.strokeRect(obj.x - B / 2 - 3, obj.y - B / 2 - 3, B + 6, B + 6);
    ctx.setLineDash([]);
  }

  // --- Object shapes -----------------------------------------------------------

  private drawObject(obj: LevelObject, level: LevelData, _ghost: boolean): void {
    const { ctx } = this;
    const accent = level.settings.accentColor;
    const hazard = level.settings.hazardColor;
    const half = B / 2;
    ctx.save();
    ctx.translate(obj.x, obj.y);
    if (obj.rot) ctx.rotate((-obj.rot * Math.PI) / 180);
    if (obj.flipY) ctx.scale(1, -1);
    if (obj.flipX) ctx.scale(-1, 1);

    switch (obj.type) {
      case 'block':
        ctx.fillStyle = '#141b36';
        ctx.strokeStyle = accent;
        ctx.lineWidth = 1.5;
        ctx.fillRect(-half, -half, B, B);
        ctx.strokeRect(-half + 1, -half + 1, B - 2, B - 2);
        break;
      case 'spike':
        ctx.fillStyle = hazard;
        ctx.beginPath();
        ctx.moveTo(-half + 3, -half);
        ctx.lineTo(0, half - 2);
        ctx.lineTo(half - 3, -half);
        ctx.closePath();
        ctx.fill();
        break;
      case 'slope': {
        const grade = typeof obj.props?.grade === 'number' ? obj.props.grade : 1;
        const rise = B * grade;
        const up = obj.props?.dir !== 'down';
        ctx.fillStyle = '#141b36';
        ctx.strokeStyle = accent;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        if (up) {
          ctx.moveTo(-half, -half);
          ctx.lineTo(half, -half);
          ctx.lineTo(half, -half + rise);
        } else {
          ctx.moveTo(-half, -half);
          ctx.lineTo(-half, -half + rise);
          ctx.lineTo(half, -half);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        break;
      }
      case 'oneway':
        ctx.strokeStyle = accent;
        ctx.lineWidth = 3;
        ctx.setLineDash([7, 4]);
        ctx.beginPath();
        ctx.moveTo(-half + 2, half - 3);
        ctx.lineTo(half - 2, half - 3);
        ctx.stroke();
        ctx.setLineDash([]);
        break;
      case 'portal': {
        const kind = typeof obj.props?.kind === 'string' ? obj.props.kind : '';
        const color = portalColor(kind);
        ctx.strokeStyle = color;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.ellipse(0, 0, 11, 42, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 0.2;
        ctx.fillStyle = color;
        ctx.fill();
        ctx.globalAlpha = 1;
        // Label letter (readable at editor zoom levels).
        ctx.save();
        ctx.scale(1, -1);
        ctx.fillStyle = color;
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(portalGlyph(kind), 0, 0);
        ctx.restore();
        break;
      }
      case 'pad': {
        ctx.fillStyle = '#fde047';
        ctx.beginPath();
        ctx.ellipse(0, -half + 4, 13, 5, 0, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case 'orb': {
        const kind = typeof obj.props?.kind === 'string' ? obj.props.kind : '';
        ctx.strokeStyle = '#fde047';
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(0, 0, 9, 0, Math.PI * 2);
        ctx.stroke();
        ctx.save();
        ctx.scale(1, -1);
        ctx.fillStyle = '#fde047';
        ctx.font = 'bold 9px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(kind.slice(0, 1).toUpperCase(), 0, 0);
        ctx.restore();
        break;
      }
      case 'endgate':
        ctx.fillStyle = 'rgba(163, 230, 53, 0.5)';
        ctx.fillRect(-5, -2.5 * B, 10, 5 * B);
        break;
      case 'checkpoint':
        ctx.strokeStyle = '#a3e635';
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.moveTo(0, -40);
        ctx.lineTo(0, 40);
        ctx.stroke();
        ctx.fillStyle = '#a3e635';
        ctx.beginPath();
        ctx.moveTo(0, 40);
        ctx.lineTo(14, 32);
        ctx.lineTo(0, 24);
        ctx.closePath();
        ctx.fill();
        break;
      case 'startpos':
        ctx.strokeStyle = '#f0abfc';
        ctx.lineWidth = 2;
        ctx.strokeRect(-half + 2, -half + 2, B - 4, B - 4);
        ctx.save();
        ctx.scale(1, -1);
        ctx.fillStyle = '#f0abfc';
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('S', 0, 0);
        ctx.restore();
        break;
      case 'decoration':
        ctx.strokeStyle = accent;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, -half);
        ctx.lineTo(0, half + 60);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(0, half + 60, 4, 0, Math.PI * 2);
        ctx.fillStyle = accent;
        ctx.fill();
        break;
      case 'collectible': {
        const ckind = typeof obj.props?.kind === 'string' ? obj.props.kind : 'shard';
        const color = ckind === 'key' ? '#fde047' : ckind === 'shard' ? '#a3e635' : '#e2e8f0';
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.lineWidth = 2;
        if (ckind === 'key') {
          ctx.beginPath();
          ctx.arc(-4, 0, 5, 0, Math.PI * 2);
          ctx.stroke();
          ctx.fillRect(1, -1.5, 10, 3);
        } else if (ckind === 'shard') {
          ctx.save();
          ctx.rotate(Math.PI / 4);
          ctx.fillRect(-6, -6, 12, 12);
          ctx.restore();
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, 7, 0, Math.PI * 2);
          ctx.stroke();
        }
        break;
      }
      case 'lock':
        ctx.fillStyle = '#3f2d0c';
        ctx.strokeStyle = '#fde047';
        ctx.lineWidth = 2;
        ctx.fillRect(-half, -half, B, B);
        ctx.strokeRect(-half + 1, -half + 1, B - 2, B - 2);
        ctx.fillStyle = '#fde047';
        ctx.beginPath();
        ctx.arc(0, 2, 3, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'text': {
        const txt = typeof obj.props?.text === 'string' ? obj.props.text : 'text';
        const size = typeof obj.props?.size === 'number' ? obj.props.size : 1;
        ctx.save();
        ctx.scale(1, -1);
        ctx.fillStyle = accent;
        ctx.font = `700 ${Math.round(16 * size)}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(txt, 0, 0);
        ctx.restore();
        break;
      }
      case 'trigger': {
        // Diamond with the trigger kind's initial. Editor-only visual.
        const kind = typeof obj.props?.kind === 'string' ? obj.props.kind : '?';
        ctx.strokeStyle = '#fbbf24';
        ctx.lineWidth = 2;
        ctx.save();
        ctx.rotate(Math.PI / 4);
        ctx.strokeRect(-9, -9, 18, 18);
        ctx.restore();
        ctx.save();
        ctx.scale(1, -1);
        ctx.fillStyle = '#fbbf24';
        ctx.font = 'bold 9px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(kind.replace('camera:', 'c').slice(0, 2).toUpperCase(), 0, 0);
        ctx.restore();
        break;
      }
    }
    ctx.restore();
  }
}

function portalGlyph(kind: string): string {
  if (kind.startsWith('form:')) return kind.slice(5, 6).toUpperCase();
  if (kind === 'gravity:up') return '↑';
  if (kind === 'gravity:down') return '↓';
  if (kind.startsWith('speed:'))
    return { half: '½', one: '1', two: '2', three: '3', four: '4' }[kind.slice(6)] ?? '?';
  if (kind === 'size:mini') return 'm';
  if (kind === 'size:normal') return 'N';
  if (kind === 'mirror:on') return '⇄';
  if (kind === 'mirror:off') return '=';
  if (kind === 'dual:on') return '2';
  if (kind === 'dual:off') return '1';
  if (kind === 'teleport') return '◎';
  return '?';
}
